/**
 * Planned versus actual, for one stop (119, ref 23411385).
 *
 * ⭐ Pure and separate from the component for the same reason `v2/schedule/timeline.ts` is:
 * `apps/web` runs its tests in node, so the arithmetic that decides what a tech reads about
 * their own hours is the half a test can hold to account. What is left in the component is
 * markup.
 *
 * 🔴 **The whole difficulty here is NULLS, not arithmetic.** Every one of the four timestamps
 * is nullable, and the wrong answer for each is a plausible-looking zero:
 *  · no `scheduledEndAt` → the plan has no LENGTH. Neither creation sheet sets an end, so
 *    this is the common case, not an edge — and "0m planned" would be a lie about the office.
 *  · no `actualStartAt` → the work has not begun. "0m elapsed" reads as *started and
 *    instant*, which is the opposite fact.
 *  · `actualEndAt` with no `actualStartAt` → a broken pair; report nothing rather than a
 *    number computed from one end.
 * The ticket's acceptance says it outright: no zeros, no "0m elapsed".
 */

export interface VisitTimes {
  scheduledStartAt: Date | string | null;
  scheduledEndAt: Date | string | null;
  actualStartAt: Date | string | null;
  actualEndAt: Date | string | null;
}

/** A span the UI can render, or null when the underlying fact does not exist. */
export interface Timing {
  /** Minutes the office booked, or null when no end was ever set. */
  plannedMinutes: number | null;
  /** Minutes actually spent so far, or null when the work has not started. */
  actualMinutes: number | null;
  /** True while the clock is still running — the caller re-renders to keep it live. */
  running: boolean;
  /**
   * Actual minus planned, ONLY when both are real numbers.
   *
   * ⚠️ Null when either side is missing, and that is the point: a variance against a plan
   * with no length is a number with no meaning, and it is exactly the kind of figure that
   * gets quoted back at somebody in a review. Same rule 113 applied when it deleted
   * utilization % for having no denominator.
   */
  varianceMinutes: number | null;
}

const at = (v: Date | string | null): number | null => {
  if (v === null) return null;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
};

/**
 * ⚠️ Clamped at zero, never negative.
 *
 * `scheduled_end_at` and `actual_end_at` have NO CHECK forcing them after their starts —
 * both are plain nullable timestamps (`app.ts:435-439`). `fleet.ts` hit this first and
 * records the reasoning: a row saved back-to-front would otherwise SUBTRACT from a total,
 * which is the one arithmetic error nobody reading the number would ever suspect.
 */
const minutesBetween = (from: number, to: number): number =>
  Math.max(0, Math.round((to - from) / 60_000));

export function timingOf(v: VisitTimes, now: number = Date.now()): Timing {
  const ps = at(v.scheduledStartAt);
  const pe = at(v.scheduledEndAt);
  const as = at(v.actualStartAt);
  const ae = at(v.actualEndAt);

  const plannedMinutes = ps !== null && pe !== null ? minutesBetween(ps, pe) : null;

  /**
   * ⚠️ An end WITHOUT a start yields null, not a number.
   *
   * `VISIT_STAMPS` writes `actual_start_at` on `visit.start` and `actual_end_at` on
   * `visit.complete`, so the pair should always arrive together — but a visit completed
   * through some path that skipped start would otherwise be measured from the epoch, and
   * "29,000,000 minutes" is a number this screen would happily print.
   */
  const actualMinutes =
    as === null ? null : ae !== null ? minutesBetween(as, ae) : minutesBetween(as, now);

  const running = as !== null && ae === null;

  return {
    plannedMinutes,
    actualMinutes,
    running,
    varianceMinutes:
      plannedMinutes !== null && actualMinutes !== null
        ? actualMinutes - plannedMinutes
        : null,
  };
}

/**
 * A count of minutes as "2h 00m" or "45m" — the ARITHMETIC only, with no opinion about
 * what the span means.
 *
 * ⭐ Exported so the next-stop countdown (117) can spell a duration the same way this
 * screen does without copying the maths. The two callers need different WORDS at the
 * edges — an elapsed clock under a minute reads "just started", a countdown under a
 * minute reads "now" — and that difference belongs in each vocabulary, not in a shared
 * function trying to serve both. One arithmetic, two vocabularies.
 */
export function hoursMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

/**
 * "2h 00m", "45m", "just started".
 *
 * ⚠️ Under a minute reads as "just started" rather than "0m". A live clock that says zero
 * looks like missing data — the same call `TechVisitPage`'s running-timer line already makes
 * for `formatMinutes`, reused in words here so the two lines on one screen agree.
 */
export function durationLabel(minutes: number | null): string {
  if (minutes === null) return '—';
  if (minutes < 1) return 'just started';
  return hoursMinutes(minutes);
}

/**
 * How the variance reads to a person: "18m over", "12m under", "on time".
 *
 * ⚠️ "on time" is the label for EXACTLY zero, and the zero is real here — it means the two
 * durations matched, not that a duration is missing. That is the one place in this module a
 * zero is allowed to be printed, which is why it gets its own word rather than "0m over".
 */
export function varianceLabel(minutes: number | null): string | null {
  if (minutes === null) return null;
  if (minutes === 0) return 'on time';
  return minutes > 0
    ? `${durationLabel(minutes)} over`
    : `${durationLabel(-minutes)} under`;
}
